/**
 * Post-answer resolution verifier for ask_user requests answered over the
 * live WebSocket.
 *
 * The socket answer path sends `askUserRequestId` + `answers` on the prompt
 * frame and trusts the backend closeout to mark the AskUserRequest answered
 * and resolve the inbox item. Every failure mode of that closeout is silent
 * (the turn continues on raw text and the request stays `pending` forever —
 * which resurrects the question card on reload). This verifier polls the
 * inbox item after a socket answer and, if the request is still pending,
 * resolves it via REST `/answer` with `continueOverWs: true` — which flips
 * the request + inbox item and publishes the resolution events WITHOUT
 * dispatching a follow-up run (the turn already continued over the socket).
 *
 * Module-scope and fire-and-forget: callers do not await it, so it survives
 * component unmount and navigation. Best-effort: all failures are swallowed
 * (the inbox remains the manual fallback), except a 409 from `/answer`,
 * which means the request reached a terminal state in the race — the
 * backend's conditional state transition guarantees only one resolution can
 * apply, so the loser's 409 is success from our perspective.
 */

import { meApi, askUserApi, ApiError } from '@/services/api';
import type { AskUserAnswer } from '@/types/inbox';

const DEFAULT_DELAYS_MS = [3000, 4000, 5000];

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface VerifyAskUserResolutionParams {
  spaceId: string;
  askUserRequestId: string;
  inboxItemId: string;
  answers: AskUserAnswer[];
}

export async function verifyAskUserResolution(
  { spaceId, askUserRequestId, inboxItemId, answers }: VerifyAskUserResolutionParams,
  opts?: { delaysMs?: number[] },
): Promise<void> {
  const delays = opts?.delaysMs ?? DEFAULT_DELAYS_MS;
  try {
    for (const delay of delays) {
      await sleep(delay);
      try {
        const item = await meApi.inboxItem(inboxItemId, { expand: 'source' });
        const stillPending =
          item.status === 'pending' &&
          (item.source?.status ?? 'pending') === 'pending';
        if (!stillPending) return; // WS closeout won — nothing to do.
      } catch {
        // Transient read failure — keep polling; the REST fallback below is
        // conditional anyway.
      }
    }
    await askUserApi.answer(spaceId, askUserRequestId, answers, {
      continueOverWs: true,
    });
    console.warn(
      `[ask-user-verify] WS closeout did not resolve ${askUserRequestId}; resolved via REST fallback`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) return; // already terminal
    console.warn(
      `[ask-user-verify] could not verify/resolve ${askUserRequestId}:`,
      err,
    );
  }
}
